import { Box } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { EQuestIcon, TQuest, useQuestsStore } from '../../stores';
import theme, { TMaterialStyles } from '../../theme';
import { claimDailyQuests, getDailyQuests } from '../../queries';
import TypographyWithShadow from '../Typography/TypographyWithShadow';
import WebApp from '@twa-dev/sdk';
import { Icon, TIcons } from '../Icon';
import { getOriginAppUrl } from '../../helpers';
import _ from 'lodash';
import { useDeepLinkNavigate } from '../../hooks';
import { ButtonWithShadow } from '../Buttons/ButtonWithShadow';

interface IQuestItem {
  quest: TQuest;
}

export const QuestItem: React.FC<IQuestItem> = ({ quest }) => {
  const { makeQuestStatusCompletedById } = useQuestsStore();
  const deepLinkNavigateByPathname = useDeepLinkNavigate();

  const questIconName: TIcons = useMemo(() => {
    switch (quest.icon) {
      case EQuestIcon.INTERNAL:
        return 'CaseIcon';
      case EQuestIcon.TELEGRAM:
        return 'TelegramIcon';
      case EQuestIcon.TWITTER:
        return 'TwitterIcon';
      case EQuestIcon.WALLET:
        return 'WalletQuestIcon';
      case EQuestIcon.REFERRAL:
        return 'ReferralQuestIcon';
    }
  }, [quest.icon]);

  const isQuestActionAvailable = useMemo(
    () => Boolean(quest.link && !quest.isCompleted),
    [quest.isCompleted, quest.link],
  );

  const onClaimQuest = useCallback(async () => {
    try {
      if (!quest.link) {
        return;
      }

      const appOriginUrl = getOriginAppUrl();

      switch (true) {
        case _.includes(quest.link, appOriginUrl): {
          const url = new URL(quest.link);
          deepLinkNavigateByPathname(url.pathname);
          break;
        }
        case WebApp.platform === 'unknown': {
          window.open(quest.link, '_blank');
          await claimDailyQuests(quest.name);
          break;
        }
        default:
          WebApp.openLink(quest.link);
          await claimDailyQuests(quest.name);
          break;
      }

      await getDailyQuests();
      makeQuestStatusCompletedById(quest.id);
    } catch (error: any) {
      console.error('Error while [onClaimQuest]', error);
    }
  }, [
    deepLinkNavigateByPathname,
    makeQuestStatusCompletedById,
    quest.id,
    quest.link,
    quest.name,
  ]);

  return (
    <ButtonWithShadow
      disabled={!isQuestActionAvailable}
      onClick={onClaimQuest}
      type={quest.isCompleted ? 'DARK' : 'PRIMARY'}
      sx={materialStyles.container}
    >
      <Box
        sx={[
          materialStyles.iconContainer,
          quest.isCompleted ? materialStyles.completedIconContainer : null,
        ]}
      >
        <Icon iconName={questIconName} sx={materialStyles.questIcon} />
      </Box>
      <Box sx={materialStyles.titleWithSubtitleContainer}>
        <TypographyWithShadow
          sx={materialStyles.descriptionText}
          shadowType="DARK"
        >
          {quest.name}
        </TypographyWithShadow>
        <Box sx={materialStyles.pointsContainer}>
          <Icon sx={materialStyles.pointsIcon} iconName="BlueScoreIcon" />
          <TypographyWithShadow
            shadowType="DARK"
            sx={materialStyles.pointsText}
          >
            {`+ ${quest.rewardAmount}`}
          </TypographyWithShadow>
        </Box>
      </Box>
      {quest.isCompleted ? (
        <Icon iconName="CompletedIcon" sx={materialStyles.completedIcon} />
      ) : null}
      {isQuestActionAvailable ? (
        <Icon iconName="ArrowRightIcon" sx={materialStyles.arrowRightIcon} />
      ) : null}
    </ButtonWithShadow>
  );
};

const materialStyles: TMaterialStyles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '8px',
    width: '100%',
    px: '22px',
    py: '13px',
    mb: '13px',
  },
  iconContainer: {
    width: '44px',
    height: '44px',
    borderRadius: '7px',
    background: theme.palette.secondary.main,
    boxShadow: `0px 4px 0px ${theme.palette.secondary.dark}`,
    mr: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  questIcon: {
    width: '30px',
    height: '30px',
  },
  completedIconContainer: {
    background: theme.palette.grey[700],
    boxShadow: `0px 4px 0px ${theme.palette.secondary.dark}`,
  },
  descriptionText: {
    fontWeight: '600',
    fontSize: '16px',
    lineHeight: '100%',
    textAlign: 'left',
  },
  titleWithSubtitleContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  pointsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    mt: '8px',
  },
  pointsText: {
    fontFamily: 'Rubber Duck !important',
    textTransform: 'uppercase',
    fontSize: '14px',
    lineHeight: '100%',
  },
  pointsIcon: {
    width: '17px',
    height: '17px',
  },
  completedIcon: {
    width: '25px',
    height: '19px',
  },
  arrowRightIcon: {
    height: '13px',
    width: '6px',
  },
};
